import React, { useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Banner, Field } from "../../components/mui";
import { MdWarningAmber } from "react-icons/md";

/**
 * 忘記密碼（設計稿 el-pm-template_4.html:2276-2303 #screen-forgot）
 *
 * ── 為什麼這一頁跟設計稿不一樣 ──────────────────────────────────
 * 設計稿寫的是「系統沒開通寄信，送出後由管理員幫你重設，必要時打電話給你」，
 * 所以才會有「分機或手機」欄位。但後端現在**真的會寄信**，以後端為準：
 *
 *   Auth/Controllers/AuthController.cs L249  [Route("Password/Forgot")] [AllowAnonymous]
 *       → IPasswordResetService.Forgot(ReqForgotPassword)
 *       → 成功回 200「若該帳號存在且已設定 Email，重設連結已寄出」
 *   Auth/Services/PasswordResetService.cs  Forgot()
 *       → 讀 smtp.* 設定，用 Utility/Mail/SmtpSender 寄出一封信，
 *         信裡是 {smtp.base_url}/Auth/ResetPassword?token=...
 *       → token 內含 5 分鐘到期時間，並綁「目前密碼雜湊的指紋」，
 *         所以密碼一改連結就失效 = 只能用一次
 *
 * 因此這一頁只需要 Account，**沒有留「分機或手機」欄位** ——
 * ReqForgotPassword 只有 { Account, Exp } 兩個欄位，後端根本收不到分機，
 * 留著等於讓人填一個不會被送到任何地方的資料，再據此期待有人打電話來。
 * 同理，Email 欄位改成「帳號或 Email」：後端是
 * `a.Account == key || a.Email == key`，兩種都查得到，不必逼人記得是哪個。
 *
 * ── 成功訊息為什麼講得這麼保留 ──────────────────────────────────
 * 後端對「查無帳號 / 帳號停用 / 帳號沒填 Email」一律安靜結束照回 200
 * （註解寫明：回報查無此帳號等於提供帳號列舉的管道）。
 * 前端不知道信到底有沒有寄出去，所以**不能**寫死「已寄到 xxx@…」，
 * 只能照後端那句話的語氣講條件句，並把「沒收到怎麼辦」寫清楚。
 *
 * ── 失敗只走一般錯誤提示 ────────────────────────────────────────
 * 舊版把失敗寫死成「系統目前沒有辦法記下這個申請，請打分機 168」，
 * 那是誤判端點不存在時的權宜寫法。端點在，失敗是真的失敗，
 * 而且後端的訊息本身就帶解法（例如「系統尚未啟用郵件通知，請聯絡管理員
 * 在『偏好設定 ▸ 郵件通知』完成設定」），sagas/Auth.js 也已經把
 * 「寄送失敗: 」前綴去掉，照實顯示就好。
 *
 * 整頁只有「寄送重設連結」是主要按鈕（硬規則 1）。
 */
function ForgotPassword(props) {
  const navigate = useNavigate();
  const { POST_ForgotPassword } = props;
  /* 後端收的欄位就叫 Account，且帳號或 Email 都接受，變數名跟著它走 */
  const [account, setAccount] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (loading) return;
    if (!account.trim()) {
      /* 後端 Forgot() 第一行就是 key.Length == 0 → throw「請輸入帳號或 Email」，
         這裡先擋是為了少跑一趟，訊息跟後端對齊 */
      setErr("請輸入帳號或 Email。");
      return;
    }
    setErr("");
    POST_ForgotPassword(
      { Account: account.trim() },
      () => setSent(true),
      setLoading,
      /* 後端訊息（SMTP 沒設定、系統網址沒填…）本身就寫了下一步，原樣顯示；
         連不上伺服器時 saga 給的是「操作失敗」，補一句一般性的重試提示 */
      (msg) => setErr(msg && msg !== "操作失敗" ? msg : "送出失敗，請稍後再試。")
    );
  };

  /* 卡片外型兩種狀態共用；max-width 與留白照設計稿 L2277，width:100% 的理由同登入頁 */
  const cardSx = { width: "100%", maxWidth: 560, my: 6, mx: "auto" };
  const contentSx = { p: { xs: 3, sm: 4 } };

  if (sent) {
    return (
      <Card component="section" sx={cardSx}>
        <CardContent sx={contentSx}>
          <Typography variant="h2" component="h1">
            重設連結已送出
          </Typography>
          {/* 後端刻意不透露帳號存不存在，所以這裡只能是條件句，不能寫「已寄到 ooo」 */}
          <Banner kind="info" sx={{ mt: 2 }}>
            若 <strong>{account.trim()}</strong> 為系統中的帳號且已登記 Email，
            重設連結已寄送至該信箱。<strong>連結將於 5 分鐘後失效，且僅能使用一次</strong>，
            請於收到後盡快開啟並設定新密碼。
          </Banner>
          <Stack spacing={2} sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => navigate("/Auth/Login")}>
              返回登入畫面
            </Button>
            {/* 「沒收到」有兩種可能：信在垃圾信件匣，或帳號根本沒登記 Email
                （後者後端查不到就安靜結束，使用者這邊等不到任何回音），兩條都要講 */}
            <Typography variant="body2" color="text.secondary">
              若數分鐘後仍未收到，請先檢查垃圾信件匣；帳號未登記 Email 時系統無法寄送，
              此情況請聯絡資訊室（分機 168）。
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card component="form" onSubmit={submit} sx={cardSx}>
      <CardContent sx={contentSx}>
        <Typography variant="h2" component="h1">
          忘記密碼
        </Typography>

        {/* 「連結只有 5 分鐘、只能用一次」是這一頁最重要的後果：
            錯過就要重來一次，所以寫成橫幅而不是灰色小字（硬規則 4） */}
        <Banner kind="info" sx={{ mt: 2 }}>
          系統將寄送設定新密碼的連結至您帳號登記的 Email。
          連結<strong>將於 5 分鐘後失效，且僅能使用一次</strong>，請於收到後立即開啟。
        </Banner>

        <Stack spacing={2} sx={{ mt: 3 }}>
          {/* 後端是 a.Account == key || a.Email == key，兩種輸入都查得到 */}
          <Field
            label="帳號或公司 Email"
            htmlFor="fg-account"
            mark="req"
            help="可輸入帳號或 Email，信件將寄送至該帳號登記的 Email。"
            helpId="fg-account-help"
          >
            <TextField
              id="fg-account"
              fullWidth
              value={account}
              autoComplete="username"
              placeholder="huang 或 huang@lifeplus.tw"
              slotProps={{ htmlInput: { "aria-describedby": "fg-account-help" } }}
              onChange={(e) => setAccount(e.target.value)}
            />
          </Field>

          {/* 送不出去也是一個重要後果：不能只在角落閃一行紅字（硬規則 4）。
              內容用後端原句，因為那句話本身就寫了要找誰、去哪裡設定 */}
          {err ? (
            <Banner kind="attention" icon={<MdWarningAmber />}>
              {err}
            </Banner>
          ) : null}

          <Button variant="contained" fullWidth type="submit" aria-disabled={loading} loading={loading} loadingPosition="start">
            {loading ? "寄送中…" : "寄送重設連結"}
          </Button>

          <Button variant="text" onClick={() => navigate("/Auth/Login")}>
            返回登入畫面
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

ForgotPassword.propTypes = { POST_ForgotPassword: PropTypes.func };
ForgotPassword.defaultProps = { POST_ForgotPassword: () => {} };

const mapDispatchToProps = (dispatch) => ({
  POST_ForgotPassword(payload, callback, loading, onError) {
    dispatch({ type: "POST_ForgotPassword", payload, callback, loading, onError });
  },
});

export default connect(null, mapDispatchToProps)(ForgotPassword);
